import React, { useEffect, useRef, useState } from "react";
import { FiLock, FiMail, FiMapPin, FiPhone } from "react-icons/fi";
import { useTenant } from "../../context/TenantContext";
import "../../styles/offline-store.css";

const OfflineStoreOverlay = () => {
  const [inactiveEventReceived, setInactiveEventReceived] = useState(false);
  const overlayRef = useRef(null);
  const { tenant } = useTenant();
  const isOffline = inactiveEventReceived || tenant?.IsActive === false;
  const email = tenant?.BusinessEmail || tenant?.ContactEmail;
  const phone = tenant?.BusinessPhone || tenant?.ContactPhone;
  const address =
    tenant?.FullAddress ||
    [
      tenant?.AddressLine1,
      tenant?.AddressLine2,
      tenant?.City,
      tenant?.State,
      tenant?.PostalCode,
      tenant?.Country,
    ]
      .filter(Boolean)
      .join(", ");

  useEffect(() => {
    const handleTenantInactive = () => setInactiveEventReceived(true);
    window.addEventListener("tenant-inactive", handleTenantInactive);

    return () =>
      window.removeEventListener("tenant-inactive", handleTenantInactive);
  }, []);

  useEffect(() => {
    if (!isOffline) return undefined;

    document.body.classList.add("store-offline");
    overlayRef.current?.focus();
    const keepFocusInOverlay = (event) => {
      if (event.key !== "Tab") return;

      const focusableElements = overlayRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusableElements?.length) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === lastElement ||
          document.activeElement === overlayRef.current)
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    };
    document.addEventListener("keydown", keepFocusInOverlay, true);

    return () => {
      document.body.classList.remove("store-offline");
      document.removeEventListener("keydown", keepFocusInOverlay, true);
    };
  }, [isOffline]);

  if (!isOffline) return null;

  return (
    <div
      ref={overlayRef}
      className="offline-store-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="offline-store-title"
      aria-describedby="offline-store-description"
      tabIndex={-1}
    >
      <div className="offline-store-card">
        <div className="offline-store-brand">
          <span className="offline-store-brand-mark" aria-hidden="true">
            <FiLock />
          </span>
          <span>TOORA FLEX</span>
        </div>

        <h1 id="offline-store-title">
          {tenant?.BusinessName || "This store"} is taking a short break
        </h1>
        <p id="offline-store-description">
          This store is temporarily unavailable while its subscription is being
          renewed. Please check back soon.
        </p>

        {(email || phone || address) && (
          <section
            className="offline-store-contact"
            aria-label={`${tenant?.BusinessName || "Store"} contact information`}
          >
            <h2>Contact the store</h2>

            {phone && (
              <a href={`tel:${phone.replace(/[^\d+]/g, "")}`}>
                <FiPhone aria-hidden="true" />
                <span>{phone}</span>
              </a>
            )}
          </section>
        )}

        <div className="offline-store-divider" />
        <p className="offline-store-footer">
          We appreciate your patience while the store returns.
        </p>
      </div>
      <div className="offline-store-copyright">
        Online storefront services by Toora Flex
      </div>
      <p className="offline-store-promotion">
        Want to build your Online Store? Start today with{" "}
        <a
          href="https://tooraflex.co.za/ecommerce"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Start building your business with Toora Flex"
        >
          <span className="offline-store-promotion-toora">Toora</span>
          <span className="offline-store-promotion-flex">Flex</span>
        </a>
      </p>
    </div>
  );
};

export default OfflineStoreOverlay;
